import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';

import SlideOut from 'components/Slideout';
import usePopup from 'hooks/usePopup';
import useMetricOptions from 'hooks/useMetricOptions';
import MetricPopup from 'screens/DataManager/Metrics/MetricPopup';
import MetricSlideOut from 'screens/DataManager/Metrics/MetricSlideOut';
import metricTypeCheckers from 'types/metricTypeCheckers';

// One metric viewer for the whole tab.
//
// Every metric named on this screen — in a report's detail, in a collision
// comparison — is a link into the same slide-out, because the question they all
// raise is "what IS this metric?" and the answer is its definition, filters and
// usage. Mounted once here rather than per expanded report: the tab can have
// several open at a time and they would each carry their own copy.
//
// Mirrors the wiring on the DataManager Metrics screen (slide-out to read, Edit
// promotes to the full popup), so the two behave identically.

type OpenMetric = (metricId: string) => void;

export const MetricViewerContext = createContext<{ openMetric: OpenMetric }>({
  openMetric: () => undefined,
});

const MetricViewerProvider = ({ children }: { children: ReactNode }) => {
  const { metricOptionsLookup } = useMetricOptions();
  const [selectedMetric, setSelectedMetric] = useState<
    Metrics.NormalMetric | Metrics.CompoundMetric | undefined
  >();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const {
    isOpen: isSlideOutOpen,
    open: openSlideOut,
    close: closeSlideOut,
  } = usePopup();
  const {
    isOpen: isPopupOpen,
    open: openPopup,
    close: closePopup,
  } = usePopup();

  const openMetric = useCallback<OpenMetric>(
    (metricId) => {
      const metric = metricOptionsLookup[metricId];
      // Special metrics have no definition to show, and a missing id has no
      // metric at all — neither is offered as a link, so this only guards
      // against a stale click.
      if (!metric || metricTypeCheckers.isSpecialMetric(metric)) {
        return;
      }
      setSelectedMetric(metric);
      openSlideOut();
    },
    [metricOptionsLookup, openSlideOut],
  );

  const openMetricPopup = useCallback(
    (metric?: Metrics.NormalMetric | Metrics.CompoundMetric) => {
      setSelectedMetric(metric);
      closeSlideOut();
      openPopup();
    },
    [closeSlideOut, openPopup],
  );

  const value = useMemo(() => ({ openMetric }), [openMetric]);

  return (
    <MetricViewerContext.Provider value={value}>
      {children}
      <SlideOut isOpen={isSlideOutOpen} close={closeSlideOut}>
        {isSlideOutOpen && !!selectedMetric && (
          <MetricSlideOut
            close={closeSlideOut}
            metric={selectedMetric}
            openMetricPopup={openMetricPopup}
          />
        )}
      </SlideOut>
      <ModalTransition>
        {isPopupOpen && (
          <MetricPopup
            mode={
              selectedMetric?.type === 'compound' ? 'CompoundMetric' : 'Metric'
            }
            close={closePopup}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        )}
      </ModalTransition>
    </MetricViewerContext.Provider>
  );
};

export const useOpenMetric = (): OpenMetric =>
  useContext(MetricViewerContext).openMetric;

export default MetricViewerProvider;
